import React, { useState } from 'react';
import './DayBookConverter.css';

const DayBookConverter = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selectedFile);
    setError(null);
    setResult(null);
  };

  const convertFile = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/convert-daybook`, {
        method: 'POST',
        body: formData,
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response. Please check if the backend is running correctly.');
      }

      // Read response only once
      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data);
      } else {
        setError(data.detail || data.error || 'Failed to convert file');
      }
    } catch (err) {
      setError('An error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (result && result.download_url) {
      try {
        const response = await fetch(`${BACKEND_URL}${result.download_url}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError('Failed to download PDF: ' + err.message);
      }
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="daybook-converter">
      <div className="converter-container">
        <div className="header">
          <h1>📊 DayBook to Extract PDF Converter</h1>
          <p>Convert your DayBook Excel files to formatted Extract PDFs instantly</p>
        </div>

        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput').click()}
        >
          <div className="upload-icon">📁</div>
          <div className="upload-text">Click to upload or drag & drop</div>
          <div className="upload-hint">Supports .xlsx and .xls files (max 16MB)</div>
          <input
            id="fileInput"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </div>

        {file && (
          <div className="selected-file">
            <div className="file-info">
              <div>
                <div className="file-name">✓ {file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
              <button className="btn btn-secondary" onClick={clearFile}>
                Change File
              </button>
            </div>
          </div>
        )}

        <div className="action-area">
          <button
            className="btn btn-primary"
            onClick={convertFile}
            disabled={!file || loading}
          >
            {loading ? 'Processing...' : 'Generate PDF'}
          </button>
        </div>

        {loading && (
          <div className="loading-area">
            <div className="spinner"></div>
            <p>Processing your DayBook file...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <strong>✗ Error:</strong> {error}
          </div>
        )}

        {result && (
          <div className="success-area">
            <div className="success-message">
              <strong>✓ {result.message}</strong>
              <p>Date: {result.date}</p>
            </div>

            <div className="stats">
              <div className="stat-box">
                <div className="stat-value">{result.stats.gst_sales}</div>
                <div className="stat-label">GST Sales</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{result.stats.credit_sales}</div>
                <div className="stat-label">Credit Sales</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{result.stats.transfer_outward}</div>
                <div className="stat-label">Transfer Outward</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">₹{parseFloat(result.stats.grand_total || result.stats.total_basic).toLocaleString()}</div>
                <div className="stat-label">Grand Total</div>
              </div>
            </div>

            <div style={{textAlign: 'center'}}>
              <button className="btn btn-success" onClick={downloadPDF}>
                📥 Download PDF
              </button>
              <p style={{marginTop: '10px', fontSize: '14px', color: '#7f8c8d'}}>
                Or <a href={`${BACKEND_URL}${result.download_url}`} target="_blank" rel="noopener noreferrer" style={{color: '#667eea'}}>click here</a> to open in new tab
              </p>
            </div>
          </div>
        )}

        <div className="footer">
          © 2025 DayBook Converter | Version 2.0
        </div>
      </div>
    </div>
  );
};

export default DayBookConverter;
